import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { SubmissionStats } from '../hooks/useMySubmissionStatus';
import { C } from '../theme/participantTokens';
import {
  cardBorderColor,
  classifySession,
  formatShortDate,
  intentLabel,
  isSessionLive,
  isValueSession,
  toolDisplayName,
} from '../utils/sessionHelpers';
import { SubmissionDonut } from './layout/SubmissionDonut';

interface SessionCardProps {
  session: PrioritizationSession;
  stats?: SubmissionStats;
  onClick: () => void;
  /** Optional due label override (e.g. from gap stats) */
  dueLabel?: string;
}

function statusLabel(s: PrioritizationSession, _stats?: SubmissionStats) {
  const group = classifySession(s);
  if (group === 'completed') {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.green }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
        Completed
      </span>
    );
  }
  if (group === 'upcoming') {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.textTer }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.slate, flexShrink: 0 }} />
        Scheduled
      </span>
    );
  }
  if (group === 'live' || isSessionLive(s)) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.coral }}>
        <span className="pp-live-dot" style={{ width: 5, height: 5, borderRadius: '50%', background: C.coral, flexShrink: 0 }} />
        Live Now
      </span>
    );
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono', fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: C.mustard }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.mustard, flexShrink: 0 }} />
      Awaiting Input
    </span>
  );
}

function myStatusLabel(stats: SubmissionStats | undefined, group: ReturnType<typeof classifySession>) {
  if (!stats || stats.loading) return null;
  if (group === 'completed') return null;
  if (stats.mySubmitted) {
    return <span style={{ color: C.green }}><span className="pp-sd" style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, display: 'inline-block', marginRight: 5 }} />Submitted</span>;
  }
  if (stats.myInProgress) {
    return <span style={{ color: C.mustard }}><span className="pp-sd" style={{ width: 5, height: 5, borderRadius: '50%', background: C.mustard, display: 'inline-block', marginRight: 5 }} />In progress</span>;
  }
  return <span style={{ color: C.coral }}><span className="pp-sd" style={{ width: 5, height: 5, borderRadius: '50%', background: C.coral, display: 'inline-block', marginRight: 5 }} />Not started</span>;
}

export function SessionCard({ session, stats, onClick, dueLabel }: SessionCardProps) {
  const group = classifySession(session);
  const borderColor = cardBorderColor(group);
  const isCompleted = group === 'completed';
  const valueSession = isValueSession(session);
  const itemsWithGaps = stats?.itemsWithGaps;
  const showValueGaps = valueSession && typeof itemsWithGaps === 'number' && !isCompleted;
  const showDonut = !valueSession && stats && stats.totalParticipants > 0 && !isCompleted;

  const ctaLabel = isCompleted
    ? 'View Results →'
    : valueSession && !stats?.mySubmitted
      ? (stats?.myInProgress ? 'Continue values →' : 'Provide values →')
      : group === 'live' || isSessionLive(session)
        ? 'Join →'
        : stats?.myInProgress
          ? 'Continue →'
          : 'Enter Session →';

  const ctaColor = group === 'live' ? C.coral : C.mustard;
  const ctaBorder = group === 'live' ? 'rgba(192,83,58,0.4)' : 'rgba(212,160,23,0.4)';

  const metaLine = showValueGaps
    ? itemsWithGaps === 0
      ? 'Values complete'
      : `Values needed — ${itemsWithGaps} item${itemsWithGaps === 1 ? '' : 's'}${dueLabel ? `, due ${dueLabel}` : ''}`
    : null;

  return (
    <div
      className="pp-anim pp-session-card"
      onClick={onClick}
      style={{
        background: C.surface,
        border: `1px solid ${C.rule}`,
        borderLeft: `2px solid ${borderColor}`,
        borderRadius: 4,
        padding: '14px 16px',
        cursor: 'pointer',
        opacity: group === 'upcoming' ? 0.7 : 1,
        transition: 'background 0.15s, border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textDim, marginBottom: 5, flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(212,160,23,0.75)' }}>{intentLabel(session)}</span>
            {session.trackId && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{session.trackId}</span>
              </>
            )}
            <span style={{ opacity: 0.4 }}>·</span>
            {statusLabel(session, stats)}
          </div>
          <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1rem', fontWeight: 400, color: C.textPri, lineHeight: 1.3 }}>
            {session.name}
          </div>
          {metaLine && (
            <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.82rem', fontWeight: 400, color: itemsWithGaps === 0 ? C.green : C.mustard, marginTop: 6, lineHeight: 1.35 }}>
              {metaLine}
            </div>
          )}
        </div>
        {showDonut && (
          <SubmissionDonut
            submitted={stats!.submittedCount}
            total={stats!.totalParticipants}
            color={group === 'live' ? C.coral : C.mustard}
          />
        )}
        {isCompleted && stats && stats.totalParticipants > 0 && !valueSession && (
          <SubmissionDonut
            submitted={stats.submittedCount}
            total={stats.totalParticipants}
            color={C.green}
            label={`${Math.round((stats.submittedCount / stats.totalParticipants) * 100)}%`}
          />
        )}
        {group === 'upcoming' && session.scheduledFor && (
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.1em', color: C.textDim, whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2 }}>
            {formatShortDate(session.scheduledFor)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.78rem', fontWeight: 300, color: C.textTer, margin: '6px 0 10px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {session.itemIds?.length ?? 0} items
        </div>
        {session.modeConfig?.expectedDuration && (
          <div>~{session.modeConfig.expectedDuration} min</div>
        )}
        {session.selectedToolId && !valueSession && (
          <div style={{ color: 'rgba(212,160,23,0.6)' }}>{toolDisplayName(session)}</div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${C.rule}` }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.07em', color: C.textDim }}>
          {valueSession && stats && typeof stats.itemsWithGaps === 'number' ? (
            <span style={{ color: stats.itemsWithGaps === 0 ? 'rgba(58,125,86,0.85)' : 'rgba(212,160,23,0.85)' }}>
              {stats.itemsComplete ?? 0} of {stats.totalItems ?? 0} complete
            </span>
          ) : stats && stats.totalParticipants > 0 ? (
            <>
              <span style={{ color: 'rgba(58,125,86,0.85)' }}>{stats.submittedCount} submitted</span>
              <span style={{ opacity: 0.3 }}> · </span>
              <span>{Math.max(0, stats.totalParticipants - stats.submittedCount)} pending</span>
            </>
          ) : (
            `${session.participantIds?.length ?? 0} participants`
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="pp-my-status" style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono', fontSize: '0.52rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {myStatusLabel(stats, group)}
          </div>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onClick(); }}
            style={{
              height: 28,
              padding: '0 12px',
              background: 'none',
              border: `1px solid ${isCompleted ? C.ruleBright : ctaBorder}`,
              borderRadius: 3,
              cursor: 'pointer',
              fontFamily: 'JetBrains Mono',
              fontSize: '0.58rem',
              letterSpacing: '0.1em',
              color: isCompleted ? C.textTer : ctaColor,
            }}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
